import React, {Component} from 'react';
import {connect} from 'react-redux';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Chip from '@material-ui/core/Chip';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import Button from '../../components/button';
import AccessDelegations from './components/access-delegations';
import DelegateDrawer from './components/delegate-drawer';
import {
  loadDelegations, loadUsers, createDelegation, revokeDelegation, clearMessage,
  loadAccessDelegations, loadDelegablePairs, loadDelegates,
  openAccessDrawer, closeAccessDrawer, createAccessDelegation, revokeAccessDelegation,
} from './my-delegations.actions';
import './my-delegations.scss';

const STATUS_STYLE = {
  active: {backgroundColor: '#e8f5e9', color: '#2e7d32'},
  scheduled: {backgroundColor: '#e3f2fd', color: '#1565c0'},
  expired: {backgroundColor: '#f5f5f5', color: '#757575'},
  revoked: {backgroundColor: '#ffebee', color: '#c62828'},
};

class MyDelegations extends Component {
  constructor(props) {
    super(props);
    this.state = {delegatee: null, startsAt: '', expiresAt: ''};
  }

  componentDidMount() {
    this.props.loadDelegations();
    this.props.loadUsers();

    // Slice 4c. `loadDelegates` doubles as the provisioning probe: it is the only route gated on
    // `access.delegation:create`, so its 403 is how the page learns the caller cannot delegate —
    // and it must run on mount, not on drawer-open, or the button renders enabled and the user
    // discovers the refusal by being refused.
    this.props.loadAccessDelegations();
    this.props.loadDelegablePairs();
    this.props.loadDelegates();
  }

  handleCreate = () => {
    const {delegatee, startsAt, expiresAt} = this.state;
    if (!delegatee || !startsAt || !expiresAt) return;
    this.props.createDelegation({
      delegateeUserId: delegatee.id,
      startsAt: `${startsAt}T00:00:00`,
      expiresAt: `${expiresAt}T23:59:59`,
    });
    this.setState({delegatee: null, startsAt: '', expiresAt: ''});
  };

  render() {
    const {delegations, users, message, error} = this.props;
    const {delegatee, startsAt, expiresAt} = this.state;
    const canSubmit = delegatee && startsAt && expiresAt;

    // Standalone route needs .app-content-container (sidebar offset); when embedded
    // in the Manage Settings panel that wrapper already exists, so use .sub-module.
    const wrapperClass = this.props.embedded ? 'sub-module my-delegations' : 'app-content-container my-delegations';

    return (
      <div className={wrapperClass}>
        <h2>My Delegations</h2>
        <p className="subtitle">
          Hand off your data dictionary ownership to a backup while you&apos;re away. Access is
          granted only for the dates you choose and reverts automatically.
        </p>

        <div className="rounded-card delegation-form">
          <h3>Delegate my ownership</h3>
          <div className="form-row">
            <Autocomplete
              className="form-field"
              options={users}
              getOptionLabel={(u) => `${u.name}${u.email ? ` (${u.email})` : ''}`}
              value={delegatee}
              onChange={(e, v) => this.setState({delegatee: v})}
              renderInput={(params) => <TextField {...params} label="Delegate to" variant="standard" />}
            />
            <TextField
              className="form-field"
              label="From"
              type="date"
              value={startsAt}
              onChange={(e) => this.setState({startsAt: e.target.value})}
              InputLabelProps={{shrink: true}}
            />
            <TextField
              className="form-field"
              label="Until"
              type="date"
              value={expiresAt}
              onChange={(e) => this.setState({expiresAt: e.target.value})}
              InputLabelProps={{shrink: true}}
            />
            <Button label="Delegate" className="btn btn-action" onClick={this.handleCreate} disabled={!canSubmit} />
          </div>
          <div className="form-hint">All of your owned schemas (and their contents) are delegated for this period.</div>
        </div>

        <div className="rounded-card">
          <h3>Delegations</h3>
          <Table size="small" className="data-table">
            <TableHead>
              <TableRow>
                <TableCell><strong>Direction</strong></TableCell>
                <TableCell><strong>Counterparty</strong></TableCell>
                <TableCell><strong>From</strong></TableCell>
                <TableCell><strong>Until</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {delegations.length === 0 && (
                <TableRow><TableCell colSpan={6} style={{color: '#9e9e9e'}}>No delegations yet.</TableCell></TableRow>
              )}
              {delegations.map((d) => {
                const outgoing = d.direction === 'outgoing';
                const counterparty = outgoing ? d.delegatee_name : d.delegator_name;
                const canRevoke = outgoing && (d.status === 'active' || d.status === 'scheduled');
                return (
                  <TableRow key={d.id} hover>
                    <TableCell>{outgoing ? 'Outgoing' : 'Incoming'}</TableCell>
                    <TableCell>{counterparty || (outgoing ? d.delegatee_user_id : d.delegator_user_id)}</TableCell>
                    <TableCell>{new Date(d.starts_at).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(d.expires_at).toLocaleDateString()}</TableCell>
                    <TableCell><Chip size="small" label={d.status} style={STATUS_STYLE[d.status] || {}} /></TableCell>
                    <TableCell>
                      {canRevoke && (
                        <Button label="Revoke" className="btn btn-default" onClick={() => this.props.revokeDelegation(d.id)} />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Slice 4c — mockup screens S6 and S7. Below the incumbent ownership card, not merged
            with it: §1.2's two objects stay two sections. */}
        <AccessDelegations
          given={this.props.access.given}
          received={this.props.access.received}
          notProvisioned={this.props.notProvisioned}
          onDelegate={() => this.props.openAccessDrawer('delegation')}
          onNominate={() => this.props.openAccessDrawer('standing_delegation')}
          onRevoke={this.props.revokeAccessDelegation}
        />

        <DelegateDrawer
          open={Boolean(this.props.accessDrawerKind)}
          kind={this.props.accessDrawerKind}
          delegates={this.props.delegates}
          pairs={this.props.delegablePairs}
          error={this.props.accessError}
          onClose={this.props.closeAccessDrawer}
          onSubmit={this.props.createAccessDelegation}
        />

        <Snackbar
          open={Boolean(message || error)}
          autoHideDuration={4000}
          onClose={this.props.clearMessage}
          anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        >
          <Alert severity={error ? 'error' : 'success'} onClose={this.props.clearMessage}>
            {error || message}
          </Alert>
        </Snackbar>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  delegations: state.myDelegationsReducer.delegations,
  users: state.myDelegationsReducer.users,
  message: state.myDelegationsReducer.message,
  error: state.myDelegationsReducer.error,
  access: state.myDelegationsReducer.access,
  delegablePairs: state.myDelegationsReducer.delegablePairs,
  delegates: state.myDelegationsReducer.delegates,
  notProvisioned: state.myDelegationsReducer.notProvisioned,
  accessDrawerKind: state.myDelegationsReducer.accessDrawerKind,
  accessError: state.myDelegationsReducer.accessError,
});

const mapDispatchToProps = (dispatch) => ({
  loadDelegations: () => dispatch(loadDelegations()),
  loadUsers: () => dispatch(loadUsers()),
  createDelegation: (payload) => dispatch(createDelegation(payload)),
  revokeDelegation: (id) => dispatch(revokeDelegation(id)),
  clearMessage: () => dispatch(clearMessage()),
  loadAccessDelegations: () => dispatch(loadAccessDelegations()),
  loadDelegablePairs: () => dispatch(loadDelegablePairs()),
  loadDelegates: () => dispatch(loadDelegates()),
  openAccessDrawer: (kind) => dispatch(openAccessDrawer(kind)),
  closeAccessDrawer: () => dispatch(closeAccessDrawer()),
  createAccessDelegation: (payload, kind) => dispatch(createAccessDelegation(payload, kind)),
  revokeAccessDelegation: (id) => dispatch(revokeAccessDelegation(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MyDelegations);
